import EventEmitter from 'events';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isEqual from 'react-fast-compare';
import devFallbackApi from '../data/dev/fallbackApi';
import devFallbackConfig from '../data/dev/fallbackConfig';
import prodFallbackApi from '../data/prod/fallbackApi';
import prodFallbackConfig from '../data/prod/fallbackConfig';
import { FALLBACK_API_USED, FALLBACK_CONFIG_USED } from '../logging/constants';
import logger from '../logging/logger';
import parseUtils from './parseUtils';
const fallbacks = {
    fallbackApi: devFallbackApi,
    fallbackConfig: devFallbackConfig,
};
const emitter = new EventEmitter();
const myState = {
    isInitialized: false,
    tabFocus: true,
};
const applicationState = {
    init(settings = {}) {
        if (settings.env === 'production') {
            fallbacks.fallbackApi = prodFallbackApi;
            fallbacks.fallbackConfig = prodFallbackConfig;
        }
        let api = settings.api;
        if (!api) {
            logger.info({ message: FALLBACK_API_USED });
            api = fallbacks.fallbackApi;
        }
        let config = settings.config;
        if (!config) {
            logger.info({ message: FALLBACK_CONFIG_USED });
            config = fallbacks.fallbackConfig;
        }
        logger.info({
            message: 'application.state.init.debug.dimensions.issue',
            data: {
                allDimensionsToInit: settings.config?.adConfig?.map((item) => ({
                    platforms: item.platforms,
                    formats: item.formats?.map((formatItem) => ({
                        [formatItem.name]: formatItem.dimensions,
                    })),
                })),
            },
        });
        this.updateUnlockDetails(settings.unlock);
        const allSettings = {
            [this.State.api]: api,
            [this.State.config]: config,
            [this.State.isInitialized]: true,
            [this.State.isPlayingVideoAd]: false,
            [this.State.positionInfo]: {},
            [this.State.videoAdPlayed]: false,
            [this.State.dfpParams]: {},
            ...settings,
            [this.State.assetLocation]: settings.assetLocation || '/',
            [this.State.firstVisitTime]: settings.isFirstVisit ? Date.now() : 0,
            [this.State.isMobile]: !!settings.isMobile,
            [this.State.partnerId]: settings.partnerId || 'RadioTime',
        };
        forEach(allSettings, (value, setting) => {
            myState[this.State[setting]] = value;
        });
        while (this.stateUpdateQueue.length) {
            const [stateName, value] = this.dequeueStateUpdate();
            this.updateState(stateName, value);
        }
        this.setAppVisibilityEventListeners();
    },
    setAppVisibilityEventListeners() {
        const options = this.get(this.State.options, {});
        const isSlotVisibilityExperimentEnabled = parseUtils.parseBool(get(options, 'ads.pauseRefresh.enabled'));
        if (isSlotVisibilityExperimentEnabled) {
            return;
        }
        window.addEventListener('focus', () => {
            this.updateState(this.State.tabFocus, true);
        });
        window.addEventListener('blur', () => {
            this.updateState(this.State.tabFocus, false);
        });
    },
    get(stateName, defaultValue) {
        const stateValue = myState[stateName];
        return isUndefined(stateValue) ? defaultValue : stateValue;
    },
    addStateChangeListener(stateName, callback, once = false) {
        if (once) {
            emitter.once(stateName, callback);
        }
        else {
            emitter.on(stateName, callback);
        }
    },
    removeStateChangeListener(stateName, callback) {
        emitter.off(stateName, callback);
    },
    updateState(stateName, value) {
        if (!myState.isInitialized) {
            this.enqueueStateUpdate([stateName, value]);
            return;
        }
        const prevValue = myState[stateName];
        if (!isEqual(prevValue, value)) {
            myState[stateName] = value;
            emitter.emit(stateName, value, prevValue);
        }
    },
    updateUnlockDetails(unlock) {
        if (unlock?.SecondsRemaining) {
            const endDate = new Date();
            endDate.setSeconds(endDate.getSeconds() + unlock.SecondsRemaining);
            unlock.EndDate = JSON.stringify(endDate).replace(/"/g, '');
        }
        this.updateState(this.State.unlockDetails, unlock);
    },
    State: {
        volume: 'volume',
        config: 'config',
        api: 'api',
        env: 'env',
        options: 'options',
        unlock: 'unlock',
        nowPlaying: 'nowPlaying',
        profiles: 'profiles',
        rtid: 'rtid',
        isMobile: 'isMobile',
        username: 'username',
        isRegisteredUser: 'isRegisteredUser',
        playerStatus: 'playerStatus',
        positionInfo: 'positionInfo',
        tabFocus: 'tabFocus',
        isInitialized: 'isInitialized',
        selectedGuideId: 'selectedGuideId',
        currentScreen: 'currentScreen',
        lotameAudiences: 'lotameAudiences',
        subscriptionStatus: 'subscriptionStatus',
        version: 'version',
        desktopVersion: 'desktopVersion',
        assetLocation: 'assetLocation',
        onPremiumUnlockEvent: 'onPremiumUnlockEvent',
        unlockDetails: 'unlockDetails',
        language: 'language',
        countryRegionId: 'countryRegionId',
        stationLanguage: 'stationLanguage',
        isFirstVisit: 'isFirstVisit',
        firstVisitTime: 'firstVisitTime',
        categoryId: 'categoryId',
        tuneRequested: 'tuneRequested',
        gdprData: 'gdprData',
        adsEnabled: 'adsEnabled',
        adTypeRequested: 'adTypeRequested',
        playSessionId: 'playSessionId',
        isPlayingVideoAd: 'isPlayingVideoAd',
        videoAdPlayed: 'videoAdPlayed',
        partnerId: 'partnerId',
        inStreamAdInProgress: 'inStreamAdInProgress',
        audioVideoAdId: 'audioVideoAdId',
        audioVideoAdCreativeId: 'audioVideoAdCreativeId',
        ccpaData: 'ccpaData',
        ppid: 'ppid',
        ipAddress: 'ipAddress',
        canShowPersonalizedAds: 'canShowPersonalizedAds',
        dfpParams: 'dfpParams',
        videoAdContainerId: 'videoAdContainerId',
        isVmapPrerollEnabled: 'isVmapPrerollEnabled',
        blankMp3Url: 'blankMp3Url',
        isAdsWizzReady: 'isAdsWizzReady',
        isUserInUS: 'isUserInUS',
        isOptedOutOfTargeting: 'isOptedOutOfTargeting',
        isInDiscordIFrame: 'isInDiscordIFrame',
        isAudioOrVideoAdPlaying: 'isAudioOrVideoAdPlaying',
        isServingAudioOrVideoAd: 'isServingAudioOrVideoAd',
        isApsBidsRequestFinished: 'isApsBidsRequestFinished',
        videoApsCustParams: 'videoApsCustParams',
    },
    stateUpdateQueue: [],
    enqueueStateUpdate(v) {
        this.stateUpdateQueue.push(v);
    },
    dequeueStateUpdate() {
        return this.stateUpdateQueue.shift();
    },
};
export const State = applicationState.State;
export const getState = applicationState.get;
export const updateState = applicationState.updateState.bind(applicationState);
export const addStateChangeListener = applicationState.addStateChangeListener;
export const removeStateChangeListener = applicationState.removeStateChangeListener;
export default applicationState;
